/**
 * LandingPageLayout - Layout preset for event/membership landing pages
 *
 * Provides a consistent structure for landing pages:
 * - Optional modals
 * - Optional feature sections (Queue-It, Region Restrictions, etc.)
 * - Header section
 * - Main content section
 * - Optional footer section
 *
 * Each section component controls its own Section/ContentWrapper usage.
 *
 * @example
 * <LandingPageLayout
 *   modals={<EventModals />}
 *   features={<EventFeatures />}
 *   headerContent={<EventHeaderContent />}
 *   stickyHeader={<EventStickyTabs />}
 *   main={<EventMainContent />}
 *   footer={<EventFooter />}
 * />
 */

import { ReactNode } from "react";
import Flex from "@ui-kit/layout/Flex/Flex.web";

export interface LandingPageLayoutProps {
  /** Modal components (rendered outside main flow) */
  modals?: ReactNode;
  /** Feature sections (Queue-It, Schema, etc.) */
  features?: ReactNode;
  /** Header content section - controls its own Section wrapper */
  headerContent?: ReactNode;
  /** Sticky header section - controls its own StickySection wrapper */
  stickyHeader?: ReactNode;
  /** Main content area - controls its own Section wrapper */
  main: ReactNode;
  /** Footer section (cart summary, etc.) */
  footer?: ReactNode;
  /** Background color for the layout container */
  bg?: string;
}

export function LandingPageLayout({
  modals,
  features,
  headerContent,
  stickyHeader,
  main,
  footer,
  bg = "bg.subtle",
}: LandingPageLayoutProps) {
  return (
    <Flex h="full" w="full" direction="column" flex={1} bg={bg}>
      {modals}
      {features}
      {headerContent}
      {stickyHeader}
      {main}
      {footer}
    </Flex>
  );
}

// Re-export for backwards compatibility during migration
export { LandingPageLayout as DetailPageLayout };
export type { LandingPageLayoutProps as DetailPageLayoutProps };

export default LandingPageLayout;
